import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getServiceBySlug, SERVICES } from "../lib/data/services";
import { CITIES } from "../lib/data/cities";
import { setSEO } from "../lib/data/seo";
import { Reveal, RevealLines } from "../components/motion/Reveal";
import SpotlightImage from "../components/site/SpotlightImage";
import EditorialMarquee from "../components/site/EditorialMarquee";
import ContactBlock from "../components/site/ContactBlock";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    if (!service) return;
    setSEO({
      title: `${service.name} in British Columbia — ${service.tagline}`,
      description: service.overview.slice(0, 180),
      keywords: service.keywords,
      canonical: `${window.location.origin}/services/${service.slug}`,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Service",
        name: `${service.name} — British Columbia`,
        provider: {
          "@type": "SecurityService",
          name: "British Columbia Security Services",
          areaServed: "British Columbia, Canada",
        },
        description: service.overview,
        areaServed: "British Columbia",
      },
    });
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [service]);

  if (!service) return <Navigate to="/services" replace />;

  const otherServices = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 6);

  return (
    <div className="pt-24">
      {/* HERO */}
      <section className="pt-12 md:pt-20 pb-16 bg-[#F4F4F0]" data-testid="service-hero">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
          <div className="md:col-span-7">
            <Link to="/services" className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              ← All Services
            </Link>
            <div className="mt-6 font-mono text-6xl md:text-8xl text-[#D92323]/40 leading-none">
              {service.number}
            </div>
            <RevealLines
              lines={service.name.split(" ")}
              className="mt-4 font-display text-6xl md:text-8xl lg:text-9xl tracking-tighter leading-[0.9] text-[#060913]"
            />
            <p className="mt-8 max-w-xl text-lg md:text-xl text-[#5C5F66] leading-relaxed">
              {service.tagline}
            </p>
          </div>
          <div className="md:col-span-5">
            <SpotlightImage
              src={service.image}
              alt={service.name}
              caption={`${service.number} · BC`}
              className="aspect-[4/5]"
            />
          </div>
        </div>
      </section>

      <EditorialMarquee items={[service.name.toUpperCase(), "BRITISH COLUMBIA", "LICENSED · BONDED · INSURED", "24 / 7 DISPATCH"]} />

      {/* OVERVIEW */}
      <section className="bg-[#F4F4F0] py-24 md:py-32" data-testid="service-overview">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Overview
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight leading-[0.95] text-[#060913]">
              What we do,
              <br />
              plainly stated.
            </h2>
          </div>
          <div className="md:col-span-8">
            <Reveal>
              <p className="text-lg md:text-xl text-[#060913] leading-relaxed">
                {service.overview}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SCOPE + INDUSTRIES */}
      <section className="bg-white py-24 md:py-32 border-y border-black/10" data-testid="service-scope">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Scope Of Work
            </div>
            <h3 className="mt-4 font-display text-3xl md:text-5xl tracking-tight text-[#060913]">
              Included in every {service.name.toLowerCase()} contract.
            </h3>
            <ul className="mt-10 space-y-0 border-t border-black/10">
              {service.scope.map((item, i) => (
                <Reveal key={i} delay={i * 0.03}>
                  <li className="grid grid-cols-12 gap-4 py-5 border-b border-black/10">
                    <span className="col-span-2 font-mono text-xs tracking-[0.2em] text-[#D92323]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="col-span-10 text-lg text-[#060913]">{item}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <div className="md:col-span-5">
            <div className="md:sticky md:top-32">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
                Industries Served
              </div>
              <ul className="mt-6 space-y-3">
                {service.industries.map((ind) => (
                  <li
                    key={ind}
                    className="border-l-2 border-[#D92323] pl-4 py-1 text-[#060913] text-base"
                  >
                    {ind}
                  </li>
                ))}
              </ul>

              <div className="mt-10 border border-black/10 p-6">
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
                  Coverage
                </div>
                <div className="mt-3 font-display text-2xl text-[#060913] leading-tight">
                  All 50+ BC cities. One dispatch.
                </div>
                <Link
                  to="/quote"
                  className="mt-6 inline-flex items-center border border-[#060913] px-4 py-3 font-mono text-[10px] tracking-[0.22em] uppercase hover:bg-[#D92323] hover:text-white hover:border-[#D92323] transition-colors"
                >
                  Request Quote →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CITY LINKS (SEO INTERNAL LINKING) */}
      <section className="bg-[#F4F4F0] py-24 md:py-32" data-testid="service-cities">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
            Local Coverage
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight leading-[0.95] text-[#060913] max-w-3xl">
            {service.name} in every corner of British Columbia.
          </h2>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-3">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/${service.slug}-${c.slug}`}
                data-testid={`service-city-link-${c.slug}`}
                className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#060913]/70 hover:text-[#D92323] py-1"
              >
                {service.name} · {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {service.faq && service.faq.length > 0 && (
        <section className="bg-white py-24 md:py-32 border-y border-black/10" data-testid="service-faq">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
                FAQ
              </div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight text-[#060913] leading-[0.95]">
                Questions we hear every week.
              </h2>
            </div>
            <div className="md:col-span-8">
              <ul className="border-t border-black/10">
                {service.faq.map((item, i) => (
                  <li key={i} className="border-b border-black/10 py-6">
                    <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#D92323]">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-2 font-display text-2xl md:text-3xl text-[#060913] tracking-tight">
                      {item.q}
                    </div>
                    <div className="mt-3 text-[#5C5F66] text-base leading-relaxed max-w-3xl">
                      {item.a}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* OTHER SERVICES */}
      <section className="bg-[#F4F4F0] py-24 md:py-32" data-testid="service-related">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95] text-[#060913]">
              Related services.
            </h2>
            <Link to="/services" className="font-mono text-[11px] tracking-[0.22em] uppercase border-b border-[#060913] pb-1">
              All Services →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {otherServices.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`related-service-${s.slug}`}
                className="group relative overflow-hidden border border-black/10 hover:border-[#D92323] transition-colors aspect-[4/3]"
              >
                <img
                  src={s.image}
                  alt={s.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060913]/85 via-[#060913]/30 to-transparent" />
                <div className="relative h-full flex flex-col justify-between p-5 text-white">
                  <span className="font-mono text-[10px] tracking-[0.24em] uppercase">
                    {s.number}
                  </span>
                  <span className="font-display text-2xl md:text-3xl">{s.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section className="bg-[#060913] text-white py-24 md:py-32" data-testid="service-quote">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Next Step · {service.name}
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">
              Scope your {service.name.toLowerCase()} project.
            </h2>
            <p className="mt-6 text-white/70 text-base leading-relaxed max-w-md">
              Call or email — we return a tailored scope and transparent hourly
              rate the same day.
            </p>
          </div>
          <div className="md:col-span-6">
            <ContactBlock variant="dark" testid={`service-${service.slug}-contact`} />
          </div>
        </div>
      </section>
    </div>
  );
}
